import { v4 as uuid } from 'uuid';

import { datadog } from './datadog';
import { isServer } from './env-helper';

type UserId = string;
type UserIdForTrack = string; // '0'으로 앞자리 패딩된 최소 5글자짜리 user id
type Currency = string;
type Network = string;
type HashString = string;
type Version = string;

// 레거시 케이스(`flipster.closedEmergencies.v2`, `flipster.tv`) 제외하고
// `flipster.configs.버전.` 형태로 사용하기로 함
export interface LocalStorage {
  [key: `flipster.configs.v1.${UserId}.skipInstantFlipModal`]: boolean;
  [key: `flipster.configs.v1.${UserId}.skipCloseAllPositionsConfirm`]: boolean;
  [key: `flipster.configs.v1.${UserId}.${Currency}-${Network}.skipMemoRequiredConfirm`]: boolean;
  [key: `flipster.configs.v1.${UserId}.${HashString}.skipCurrencyWarningConfirm`]: boolean;
  [key: `flipster.configs.v1.${UserId}.skipPositionFullCloseModal`]: boolean;
  [key: `flipster.configs.v1.${UserId}.skipAirdropPositionCloseModal`]: boolean;
  [
    key: `flipster.configs.v1.${UserId}.skipTradeOnboarding`
  ]: boolean /** trade 페이지에서 onboading을 확인했는지 여부 */;
  [key: `flipster.configs.v1.${UserId}.recentSearchHistory`]: string[];
  [key: `flipster.configs.v1.${UserId}.pnlAnalysisRealizedOnlyMode`]: boolean;
  [key: `flipster.configs.v1.${UserId}.skipHedgingNoticeConfirm`]: boolean;
  [key: `flipster.configs.v1.${UserId}.showAllPerp`]: boolean;
  [key: `flipster.configs.v1.${UserId}.showAllSpot`]: boolean;
  [key: `flipster.configs.v1.${UserId}.assetsOverviewExpanded`]: boolean;
  [key: `flipster.configs.v1.${UserId}.assetsPerpetualTabOverviewExpanded`]: boolean;
  [key: `flipster.configs.v1.${UserId}.assetsSpotTabOverviewExpanded`]: boolean;
  [key: `flipster.configs.v1.${UserId}.skipFlipWarningInProfitBoost`]: boolean;
  [key: `flipster.configs.v1.${UserId}.skipAdditionalOrderWarningInProfitBoost`]: boolean;
  [key: `flipster.configs.v1.${UserId}.skipStakedBalanceWarning`]: boolean;
  [key: `flipster.configs.v1.${UserId}.pipMode`]: boolean;
  [key: `flipster.configs.v1.${UserId}.tncAgreementModalHideTimestamp`]: string;
  [key: `flipster.configs.v1.${UserId}.pnlMode`]: 'unrealizedPnl' | 'netPnl';
  [key: `flipster.configs.v1.${UserId}.upnlBasis`]: 'mark' | 'last' | 'bbo';
  [key: `flipster.configs.v1.${UserId}.marginBonusExpirationHideTimestamp`]: string | null;
  [key: `flipster.configs.v1.${UserId}.skipLeverageLimitRestrictPopup`]: boolean;
  [key: `flipster.configs.v1.${UserId}.skipAdjustLeverageDialog`]: boolean;
  [key: `flipster.configs.v1.amplitude.${UserIdForTrack}.variants`]: unknown; // amplitude Variants | undefined
  [key: `flipster.configs.v1.accordion.${UserId}.${string}`]: boolean;
  'flipster.configs.v1.nutsAndUserId': string;
  'flipster.configs.v1.closedAssetsPageBanner': string | number[];
  'flipster.configs.v1.closedLaunchpoolPageBanner': string[];
  'flipster.closedEmergencies.v2': number[];
  [key: `flipster.configs.v1.${UserId}.leverage`]: Record<string, number>;
  [key: `flipster.configs.v1.${UserId}.marginType`]: Record<string, 'Cross' | 'Isolated'>;
  [key: `flipster.configs.v2.${UserId}.leverage`]: Record<string, number>;
  [key: `flipster.configs.v2.${UserId}.marginType`]: Record<string, 'Cross' | 'Isolated'>;
  'flipster.configs.v1.addPositionModal.orderTypeSelect': 'qty' | 'margin' | 'positionSize';
  'flipster.configs.v1.priceTypeTab': 'market' | 'trigger' | 'limit';
  'flipster.configs.v1.kycModalHideTimestamp': string;
  'flipster.configs.v1.closeFomoBanner': number; // close한 datetime
  'flipster.configs.v1.spot.sideTypeTab': 'Buy' | 'Sell';
  'flipster.configs.v1.leaderboardLeagueOnboardingModal': boolean;
  'flipster.configs.v1.editProfileOnboarding': boolean;
  'flipster.configs.v1.profileNotificationsOnboarding': boolean;
  'flipster.configs.v1.profile.feedTabRedDot': boolean;
  'flipster.configs.v1.accountRedDot': boolean;
  'flipster.configs.v1.skipTriggerTimeTooltip': boolean;
  'flipster.configs.v1.marketOrderSlippageEnabled': boolean;
  'flipster.configs.v1.shareThisTradeChecked': boolean;
  // Share this trade in the feed 모달을 띄우는 조건을 계산
  // 1. share 하지 않은 유저에게 1회 노출
  // 2. 모달을 본 후 1주일 내에도 share하지 않으면 1회 추가 노출
  // UNSEEN -> 모달이 아직 한번도 노출되지 않음
  // READY -> timestamp 시간에 1회 노출이 이루어졌음
  // COMPLETE -> 모달이 2번 노출된 상태
  'flipster.configs.v1.shareThisTradeNudgeState': ['UNSEEN'] | ['READY', number /* timestamp */] | ['COMPLETE'];
  'flipster.configs.v1.feedTabRedDot.livePnlShoutout': boolean;
  'flipster.configs.v1.marketPulseOpen': boolean;
  'flipster.configs.v1.amplitude.variants': unknown; // amplitude Variants | undefined

  'flipster.configs.v1.trade_settings_landed_features': string[];
  'flipster.configs.v1.displayed_leverage_type': 'positionLeverage' | 'effectiveLeverage';
  'flipster.configs.v1.order_tutorial_v2_feature_tooltip': boolean;
  'flipster.configs.v1.order_tutorial_v2_new_feature_tooltip': boolean;

  'flipster.configs.v1.pip_button': boolean;
  'flipster.configs.v1.notice_upnl_earn': boolean;

  'flipster.tv.whaleTracker.red-dot': boolean;

  'flipster.configs.v1.tncAgreementModalHideTimestamp': string;
  'flipster.configs.v1.lastViewedNotificationTime': string | null;

  // profile - oauth related key
  [key: `flipster.configs.v1.${Version}.editProfileDraftForOAuth`]: unknown; // edit profile payload
  'flipster.configs.v1.x-oauth.state': string;
  'flipster.configs.v1.x-oauth.verifier': string;

  'flipster.configs.v1.prexInstallationId': string; // amplitude 에서 사용하는 prex_installation_id

  'flipster.configs.v1.cookiePopupTimestamp': string;
  'flipster.configs.v1.cookiePopupType': 'all' | 'required';
  'flipster.configs.v1.query.cache': {
    version: string;
    mutations: unknown[];
    queries: unknown[];
  };
  'flipster.configs.v1.featureHighlightVersion': string;
  [key: `flipster.configs.v1.${UserId}.dynamicEarnLiquidationRiskChecked`]: boolean;
  [key: `flipster.configs.v1.${UserId}.narsilDynamicEarnLiquidationRiskChecked`]: boolean;
  [key: `flipster.configs.v1.${UserId}.dynamicEarnMigrationModalDismissed`]: boolean;
  [key: `flipster.configs.v1.installation.${string}.dynamicEarnMigrationModalDismissed`]: boolean;
  [key: `flipster.configs.v1.${UserId}.dynamicEarnNewProductModalDismissed`]: boolean;
  [key: `flipster.configs.v1.installation.${string}.dynamicEarnNewProductModalDismissed`]: boolean;
  [key: `flipster.configs.v1.${UserId}.orderbookDisplayMode`]: 'amount' | 'qty';
  [key: `flipster.configs.v1.${UserId}.orderbookDisplayMode.${string}`]: 'amount' | 'qty';
  [key: `flipster.configs.v1.${UserId}.orderbookTickSize.${string}`]: string;

  'flipster.configs.v1.dismissTradFiBanner': boolean;
  [key: `flipster.configs.v1.bannerInline.${string}.${number}`]: boolean;
  'flipster.configs.v1.copyTrade.fixedMultiplierRedDot': boolean;
  // TradFi Tradable Off Hours(ERD-240) Alert "Don't show again" — 디바이스 저장, 백엔드 미동기화
  'flipster.configs.v1.skipOffHoursAlert': boolean;
  // TP/SL basis mode persistence — entrance × Long/Short (entire/partial split)
  [key: `flipster.configs.v1.tpslBasis.${string}`]: unknown;
  [key: `flipster.configs.v1.${UserId}.tradeLayout`]: {
    layouts: { i: string; x: number; y: number; w: number; h: number; minW?: number; minH?: number }[];
    hiddenPanels: string[];
    version: number;
  };
}

export const getLocalStorage = <T extends keyof LocalStorage>(key: T) => {
  if (isServer) return null;
  const value = localStorage?.getItem(key);
  try {
    if (!value) return null;
    return JSON.parse(value) as LocalStorage[T];
  } catch {
    return (value as LocalStorage[T]) ?? null;
  }
};

export const setLocalStorage = <T extends keyof LocalStorage>(key: T, value: LocalStorage[T]) => {
  if (isServer) return;
  try {
    localStorage?.setItem(key, typeof value === 'string' ? value : JSON.stringify(value));
  } catch (error) {
    datadog.addError(error, { source: 'setLocalStorage', key });
  }
};

export const deleteLocalStorage = <T extends keyof LocalStorage>(key: T) => {
  if (isServer) return;
  localStorage?.removeItem(key);
};

/**
 * amplitude에서 사용하는 prex_installation_id를 반환한다.
 * localStorage에 값이 있으면 그대로 사용하고, 없으면 내부에서 새로 생성해 저장한다.
 * 모든 API 요청 헤더(`X-Prex-Installation-Id`)와 AB 테스트에서 공유하는 단일 소스.
 */
export const getPrexInstallationId = (): string => {
  const key = 'flipster.configs.v1.prexInstallationId';
  const value = getLocalStorage(key);
  if (value) return value;
  // setLocalStorage는 서버에서 no-op이므로 서버에서는 매 호출마다 새 값을 반환한다(기존 동작과 동일).
  const newValue = uuid();
  setLocalStorage(key, newValue);
  return newValue;
};

/**
 * 주어진 prefix 로 시작하는 localStorage 키 중 keepKeys 에 없는 것을 모두 삭제한다.
 * 동적 키(예: 배너 id 별 dismiss 캐시)가 누적될 때 stale 값을 정리하는 용도.
 */
export const clearLocalStorageByPrefix = (prefix: string, keepKeys: string[]) => {
  if (isServer) return;
  try {
    const keep = new Set(keepKeys);
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key && key.startsWith(prefix) && !keep.has(key)) {
        localStorage.removeItem(key);
      }
    }
  } catch (error) {
    datadog.addError(error, { source: 'clearLocalStorageByPrefix', key: prefix });
  }
};

export const getFlipsterConfigsV1Key = <
  T extends
    | 'skipInstantFlipModal'
    | 'skipCloseAllPositionsConfirm'
    | 'skipPositionFullCloseModal'
    | 'skipTradeOnboarding'
    | 'recentSearchHistory'
    | 'skipHedgingNoticeConfirm'
    | 'showAllPerp'
    | 'showAllSpot'
    | 'assetsOverviewExpanded'
    | 'assetsPerpetualTabOverviewExpanded'
    | 'assetsSpotTabOverviewExpanded'
    | 'skipFlipWarningInProfitBoost'
    | 'skipAdditionalOrderWarningInProfitBoost'
    | 'skipStakedBalanceWarning'
    | 'pipMode'
    | 'skipAirdropPositionCloseModal'
    | 'pnlAnalysisRealizedOnlyMode'
    | 'tncAgreementModalHideTimestamp'
    | 'pnlMode'
    | 'upnlBasis'
    | 'leverage'
    | 'marginType'
    | 'marginBonusExpirationHideTimestamp'
    | 'skipLeverageLimitRestrictPopup'
    | 'dynamicEarnLiquidationRiskChecked'
    | 'narsilDynamicEarnLiquidationRiskChecked'
    | 'dynamicEarnMigrationModalDismissed'
    | 'dynamicEarnNewProductModalDismissed'
    | 'orderbookDisplayMode'
    | 'tradeLayout'
    | 'skipAdjustLeverageDialog',
>(
  userId: number,
  type: T,
) => `flipster.configs.v1.${userId}.${type}` as const;

export const getFlipsterConfigsV2Key = <T extends 'leverage' | 'marginType'>(userId: number, type: T) =>
  `flipster.configs.v2.${userId}.${type}` as const;
